import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { getContestDetails, enterContest } from '../api';

const ContestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participateLoading, setParticipateLoading] = useState(false);

  useEffect(() => {
    const fetchContest = async () => {
      try {
        const token = await getToken();
        const data = await getContestDetails(id, token);
        setContest(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchContest();
  }, [id, getToken]);

  const handleParticipate = async (mediaFile) => {
    try {
      setParticipateLoading(true);
      const token = await getToken();
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        await enterContest(id, base64, token);
        alert('Successfully joined the contest!');
        navigate('/contests');
      };
      reader.readAsDataURL(mediaFile);
    } catch (err) {
      alert('Error joining contest: ' + err.message);
    } finally {
      setParticipateLoading(false);
    }
  };

  if (loading) return <div className="text-center py-10 text-text-light">⏳ Loading contest details...</div>;
  if (error) return <div className="text-center py-10 text-error">❌ Error: {error}</div>;
  if (!contest) return <div className="text-center py-10 text-text-light">🔍 Contest not found</div>;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="container mx-auto max-w-2xl">
        <button
          onClick={() => navigate('/contests')}
          className="mb-4 px-4 py-2 bg-secondary text-surface rounded-lg hover:bg-secondary/90 transition"
        >
          ← Back to Contests
        </button>

        <div className="bg-surface rounded-lg shadow-lg p-8 border-t-4 border-accent">
          <h1 className="text-4xl font-bold mb-4 text-secondary">🏆 {contest.title}</h1>
          <p className="text-text-light mb-6">{contest.description}</p>

          {contest.banner_url && (
            <img src={contest.banner_url} alt={contest.title} className="w-full h-64 object-cover rounded-lg mb-6 shadow-md" />
          )}

          <div className="grid grid-cols-2 gap-4 mb-8 p-4 bg-background rounded-lg">
            <div className="p-4 border-l-4 border-accent">
              <p className="text-text-light text-sm">💵 Prize Pool</p>
              <p className="text-2xl font-bold text-accent">₦{contest.total_prize || 0}</p>
            </div>
            <div className="p-4 border-l-4 border-primary">
              <p className="text-text-light text-sm">👥 Participants</p>
              <p className="text-2xl font-bold text-primary">{contest.contestant_count || 0}</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-text font-semibold mb-3">📤 Upload Your Entry</label>
            <input
              type="file"
              accept="image/*,video/*"
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleParticipate(e.target.files[0]);
                }
              }}
              disabled={participateLoading}
              className="block w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition disabled:bg-gray-100"
            />
            {participateLoading && <p className="text-primary font-semibold mt-2">⏳ Uploading entry...</p>}
          </div>

          {contest.status && (
            <p className="text-sm text-text-light">
              <span className="font-semibold">Status:</span> <span className="font-bold text-accent">{contest.status}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContestDetailPage;
